import React, { Children, useEffect, useState } from "react"
import { createPortal } from "react-dom"

/**
 * The app's @mention textarea: a drop-in for react-mentions 4.4.10 (Messages, CommentBox,
 * MsgPanel), which is no longer maintained and leaned on React behaviour React 19 removed.
 *
 * Same API (MentionsInput + Mention, value in markup form, onChange(event, newValue,
 * newPlainTextValue, mentions), inputRef, onAdd) and the same DOM: class names derived from
 * `className` (`x__control`, `x__input`, `x__suggestions__item--focused` ...) and the same
 * inline styles, so the app's styles and the text it saves are unchanged. The suggestion list is
 * the light list the boxes already draw with renderSuggestion (avatar + name).
 * __fixtures__/react-mentions-4.4.10.json holds the library's own output; the parity test
 * replays it.
 *
 * The text logic is react-mentions', ported:
 * Copyright (c) 2018, Signavio GmbH. All rights reserved. BSD-3-Clause.
 * Redistribution and use in source and binary forms, with or without modification, are
 * permitted provided that: redistributions of source code retain the above copyright notice,
 * this list of conditions and the following disclaimer; redistributions in binary form
 * reproduce them in the documentation and/or other materials provided with the distribution;
 * neither the name Effektif nor the names of its contributors are used to endorse or promote
 * products derived from this software without specific prior written permission.
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS
 * OR IMPLIED WARRANTIES ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS
 * BE LIABLE FOR ANY DAMAGES ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE.
 */

// ---- markup <-> plain text ------------------------------------------------------------------

const escapeRegex = (str) => str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&")

const PLACEHOLDERS = { id: "__id__", display: "__display__" }

const MENTION_DEFAULTS = {
    trigger: "@",
    markup: "@[__display__](__id__)",
    displayTransform: (id, display) => display || id,
    onAdd: () => null,
    renderSuggestion: null,
    appendSpaceOnAdd: false,
}

// A <Mention> child's props with react-mentions' defaults filled in
const mentionProps = (child) => {
    const props = { ...MENTION_DEFAULTS }
    for (const [k, v] of Object.entries(child.props)) if (v !== undefined) props[k] = v
    return props
}

const findPositionOfCapturingGroup = (markup, parameterName) => {
    let indexDisplay = markup.indexOf(PLACEHOLDERS.display)
    let indexId = markup.indexOf(PLACEHOLDERS.id)
    if (indexDisplay < 0) indexDisplay = null
    if (indexId < 0) indexId = null
    if (indexDisplay !== null && indexId !== null) {
        return (parameterName === "id" && indexId <= indexDisplay) || (parameterName === "display" && indexDisplay <= indexId) ? 0 : 1
    }
    // one placeholder: its capture serves as both id and display
    return 0
}

const countPlaceholders = (markup) => (markup.includes(PLACEHOLDERS.id) ? 1 : 0) + (markup.includes(PLACEHOLDERS.display) ? 1 : 0)

const combineRegExps = (regExps) =>
    new RegExp(regExps.map((regex) => "(" + /^\/(.+)\/(\w+)?$/.exec(regex.toString())[1] + ")").join("|"), "g")

const markupToRegex = (markup) => {
    const escaped = escapeRegex(markup)
    const charAfterDisplay = markup[markup.indexOf(PLACEHOLDERS.display) + PLACEHOLDERS.display.length]
    const charAfterId = markup[markup.indexOf(PLACEHOLDERS.id) + PLACEHOLDERS.id.length]
    return new RegExp(
        escaped
            .replace(PLACEHOLDERS.display, `([^${escapeRegex(charAfterDisplay || "")}]+?)`)
            .replace(PLACEHOLDERS.id, `([^${escapeRegex(charAfterId || "")}]+?)`),
    )
}

const readConfigFromChildren = (children) =>
    Children.toArray(children).map((child) => {
        const { markup, regex, displayTransform } = mentionProps(child)
        return { markup, regex: regex || markupToRegex(markup), displayTransform }
    })

// Calls markupIteratee for every mention in the value and textIteratee for the text between
const iterateMentionsMarkup = (value, config, markupIteratee, textIteratee = () => {}) => {
    const regex = combineRegExps(config.map((c) => c.regex))
    let accOffset = 2
    const captureGroupOffsets = config.map(({ markup }) => {
        const result = accOffset
        accOffset += countPlaceholders(markup) + 1
        return result
    })
    let match
    let start = 0
    let currentPlainTextIndex = 0
    while ((match = regex.exec(value)) !== null) {
        const offset = captureGroupOffsets.find((o) => !!match[o])
        const mentionChildIndex = captureGroupOffsets.indexOf(offset)
        const { markup, displayTransform } = config[mentionChildIndex]
        const id = match[offset + findPositionOfCapturingGroup(markup, "id")]
        const display = displayTransform(id, match[offset + findPositionOfCapturingGroup(markup, "display")])
        const substr = value.substring(start, match.index)
        textIteratee(substr, start, currentPlainTextIndex)
        currentPlainTextIndex += substr.length
        markupIteratee(match[0], match.index, currentPlainTextIndex, id, display, mentionChildIndex, start)
        currentPlainTextIndex += display.length
        start = regex.lastIndex
    }
    if (start < value.length) textIteratee(value.substring(start), start, currentPlainTextIndex)
}

const getPlainText = (value, config) => {
    let result = ""
    iterateMentionsMarkup(
        value,
        config,
        (match, index, plainTextIndex, id, display) => (result += display),
        (plainText) => (result += plainText),
    )
    return result
}

// Index in the markup value for an index in the plain text. Inside a mention, 'START' gives the
// markup's first char, 'END' the one after its last, 'NULL' null.
const mapPlainTextIndex = (value, config, indexInPlainText, inMarkupCorrection = "START") => {
    if (typeof indexInPlainText !== "number") return indexInPlainText
    let result
    iterateMentionsMarkup(
        value,
        config,
        (markup, index, mentionPlainTextIndex, id, display) => {
            if (result !== undefined) return
            if (mentionPlainTextIndex + display.length > indexInPlainText) {
                result = inMarkupCorrection === "NULL" ? null : index + (inMarkupCorrection === "END" ? markup.length : 0)
            }
        },
        (substr, index, substrPlainTextIndex) => {
            if (result !== undefined) return
            if (substrPlainTextIndex + substr.length >= indexInPlainText) result = index + indexInPlainText - substrPlainTextIndex
        },
    )
    return result === undefined ? value.length : result
}

const spliceString = (str, start, end, insert) => str.substring(0, start) + insert + str.substring(end)

// Applies a change made to the textarea's plain text to the markup value, guided by the
// selection before and after the change
const applyChangeToValue = (value, plainTextValue, { selectionStartBefore, selectionEndBefore, selectionEndAfter }, config) => {
    const oldPlainTextValue = getPlainText(value, config)
    const lengthDelta = oldPlainTextValue.length - plainTextValue.length
    if (selectionStartBefore === "undefined") selectionStartBefore = selectionEndAfter + lengthDelta
    if (selectionEndBefore === "undefined") selectionEndBefore = selectionStartBefore

    // replacing combined characters (accented letters on macOS)
    if (
        selectionStartBefore === selectionEndBefore &&
        selectionEndBefore === selectionEndAfter &&
        oldPlainTextValue.length === plainTextValue.length
    ) {
        selectionStartBefore = selectionStartBefore - 1
    }

    let insert = plainTextValue.slice(selectionStartBefore, selectionEndAfter)
    let spliceStart = Math.min(selectionStartBefore, selectionEndAfter)
    let spliceEnd = selectionEndBefore
    // Delete key with no range selected
    if (selectionStartBefore === selectionEndAfter) spliceEnd = Math.max(selectionEndBefore, selectionStartBefore + lengthDelta)

    let mappedSpliceStart = mapPlainTextIndex(value, config, spliceStart, "START")
    let mappedSpliceEnd = mapPlainTextIndex(value, config, spliceEnd, "END")
    const willRemoveMention =
        mapPlainTextIndex(value, config, spliceStart, "NULL") === null || mapPlainTextIndex(value, config, spliceEnd, "NULL") === null
    let newValue = spliceString(value, mappedSpliceStart, mappedSpliceEnd, insert)

    if (!willRemoveMention) {
        // auto-correction changed more than the selection
        const controlPlainTextValue = getPlainText(newValue, config)
        if (controlPlainTextValue !== plainTextValue) {
            spliceStart = 0
            while (plainTextValue[spliceStart] === controlPlainTextValue[spliceStart]) spliceStart++
            insert = plainTextValue.slice(spliceStart, selectionEndAfter)
            spliceEnd = oldPlainTextValue.lastIndexOf(plainTextValue.substring(selectionEndAfter))
            mappedSpliceStart = mapPlainTextIndex(value, config, spliceStart, "START")
            mappedSpliceEnd = mapPlainTextIndex(value, config, spliceEnd, "END")
            newValue = spliceString(value, mappedSpliceStart, mappedSpliceEnd, insert)
        }
    }
    return newValue
}

// Plain-text index of the first char of the mention holding indexInPlainText, if any
const findStartOfMentionInPlainText = (value, config, indexInPlainText) => {
    let result
    iterateMentionsMarkup(value, config, (markup, index, mentionPlainTextIndex, id, display) => {
        if (mentionPlainTextIndex <= indexInPlainText && mentionPlainTextIndex + display.length > indexInPlainText) {
            result = mentionPlainTextIndex
        }
    })
    return result
}

const getMentions = (value, config) => {
    const mentions = []
    iterateMentionsMarkup(value, config, (match, index, plainTextIndex, id, display, childIndex) => {
        mentions.push({ id, display, childIndex, index, plainTextIndex })
    })
    return mentions
}

const getEndOfLastMention = (value, config) => {
    const mentions = getMentions(value, config)
    const last = mentions[mentions.length - 1]
    return last ? last.plainTextIndex + last.display.length : 0
}

const makeMentionsMarkup = (markup, id, display) => markup.replace(PLACEHOLDERS.id, id).replace(PLACEHOLDERS.display, display)

const makeTriggerRegex = (trigger, { allowSpaceInQuery } = {}) => {
    if (trigger instanceof RegExp) return trigger
    const t = escapeRegex(trigger)
    // group 1 is replaced on completion, group 2 is the search query
    return new RegExp(`(?:^|\\s)(${t}([^${allowSpaceInQuery ? "" : "\\s"}${t}]*))$`)
}

const normalizeString = (str) => str.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()

const getSubstringIndex = (str, substr, ignoreAccents) =>
    ignoreAccents ? normalizeString(str).indexOf(normalizeString(substr)) : str.toLowerCase().indexOf(substr.toLowerCase())

const getDataProvider = (data, ignoreAccents) =>
    Array.isArray(data)
        ? (query) => data.filter((item) => getSubstringIndex(item.display || item.id, query, ignoreAccents) >= 0)
        : data

const countSuggestions = (suggestions) => Object.values(suggestions).reduce((acc, { results }) => acc + results.length, 0)

const flatSuggestions = (suggestions) =>
    Object.values(suggestions).flatMap(({ results, queryInfo }) => results.map((result) => ({ result, queryInfo })))

const isNumber = (val) => typeof val === "number"

const lengthProp = (el, name) => {
    const length = parseFloat(window.getComputedStyle(el, null).getPropertyValue(name))
    return isFinite(length) ? length : 0
}

// ---- styles: react-mentions' defaults and class names ---------------------------------------

const STYLE = {
    root: { position: "relative", overflowY: "visible" },
    input: {
        display: "block",
        width: "100%",
        position: "absolute",
        margin: 0,
        top: 0,
        left: 0,
        boxSizing: "border-box",
        backgroundColor: "transparent",
        fontFamily: "inherit",
        fontSize: "inherit",
        letterSpacing: "inherit",
    },
    inputMultiLine: { height: "100%", bottom: 0, overflow: "hidden", resize: "none" },
    highlighter: {
        position: "relative",
        boxSizing: "border-box",
        width: "100%",
        color: "transparent",
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        border: "1px solid transparent",
        textAlign: "start",
    },
    substring: { visibility: "hidden" },
    suggestions: { zIndex: 1, backgroundColor: "white", marginTop: 14, minWidth: 100 },
    list: { margin: 0, padding: 0, listStyleType: "none" },
    item: { cursor: "pointer" },
    mention: { fontWeight: "inherit" },
}

const isMobileSafari = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent)
const INPUT_MULTI = { ...STYLE.input, ...STYLE.inputMultiLine, ...(isMobileSafari ? { marginTop: 1, marginLeft: -3 } : null) }
const HIGHLIGHTER_SINGLE = { ...STYLE.highlighter, whiteSpace: "pre" }
delete HIGHLIGHTER_SINGLE.wordWrap

// "x__control" etc. from the first class name, as react-mentions' substyle derived them
const classesFor = (className) => {
    const base = className ? className.split(" ")[0] : null
    return (...path) => (base ? `${base}__${path.join("__")}` : undefined)
}

// ---- the components --------------------------------------------------------------------------

export function Mention({ display, className }) {
    return (
        <strong style={STYLE.mention} className={className}>
            {display}
        </strong>
    )
}

function Highlighter({ selectionStart, selectionEnd, value = "", onCaretPositionChange, containerRef, children, singleLine, cls }) {
    const [position, setPosition] = useState({ left: undefined, top: undefined })
    const [caretElement, setCaretElement] = useState()

    useEffect(() => {
        if (!caretElement) return
        const { offsetLeft, offsetTop } = caretElement
        if (position.left === offsetLeft && position.top === offsetTop) return
        const next = { left: offsetLeft, top: offsetTop }
        setPosition(next)
        onCaretPositionChange(next)
    })

    const config = readConfigFromChildren(children)
    const caretPositionInMarkup = selectionEnd === selectionStart ? mapPlainTextIndex(value, config, selectionStart, "START") : undefined

    const resultComponents = []
    const componentKeys = {}
    let components = resultComponents
    let substringKey = 0

    const renderSubstring = (string, key) => (
        <span style={STYLE.substring} className={cls("highlighter", "substring")} key={key}>
            {string}
        </span>
    )

    iterateMentionsMarkup(
        value,
        config,
        (markup, index, indexInPlainText, id, display, mentionChildIndex) => {
            componentKeys[id] = componentKeys[id] === undefined ? 0 : componentKeys[id] + 1
            const child = Children.toArray(children)[mentionChildIndex]
            components.push(React.cloneElement(child, { id, display, key: id + "_" + componentKeys[id] }))
        },
        (substr, index) => {
            // the caret span wraps everything from the caret on, so its offset is the caret's
            if (isNumber(caretPositionInMarkup) && caretPositionInMarkup >= index && caretPositionInMarkup <= index + substr.length) {
                const split = caretPositionInMarkup - index
                components.push(renderSubstring(substr.substring(0, split), substringKey))
                components = [renderSubstring(substr.substring(split), substringKey)]
            } else {
                components.push(renderSubstring(substr, substringKey))
            }
            substringKey++
        },
    )

    // a trailing space so the last line has its height
    components.push(" ")
    if (components !== resultComponents) {
        resultComponents.push(
            <span className={cls("highlighter", "caret")} ref={setCaretElement} key="caret">
                {components}
            </span>,
        )
    }

    return (
        <div style={singleLine ? HIGHLIGHTER_SINGLE : STYLE.highlighter} className={cls("highlighter")} ref={containerRef}>
            {resultComponents}
        </div>
    )
}

function Suggestion({ id, focused, ignoreAccents, index, onClick, onMouseEnter, query, renderSuggestion, suggestion, cls }) {
    const display = typeof suggestion === "string" ? suggestion : suggestion.id === undefined || !suggestion.display ? suggestion.id : suggestion.display

    const renderHighlightedDisplay = () => {
        const i = getSubstringIndex(display, query, ignoreAccents)
        if (i === -1) return <span className={cls("suggestions", "item", "display")}>{display}</span>
        return (
            <span className={cls("suggestions", "item", "display")}>
                {display.substring(0, i)}
                <b className={cls("suggestions", "item", "highlight")}>{display.substring(i, i + query.length)}</b>
                {display.substring(i + query.length)}
            </span>
        )
    }

    const highlighted = renderHighlightedDisplay()
    const item = cls("suggestions", "item")
    return (
        <li
            id={id}
            role="option"
            aria-selected={focused}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            style={STYLE.item}
            className={item && (focused ? `${item} ${item}--focused` : item)}
        >
            {renderSuggestion ? renderSuggestion(suggestion, query, highlighted, index, focused) : highlighted}
        </li>
    )
}

function SuggestionsOverlay({
    id,
    suggestions = {},
    a11ySuggestionsListLabel,
    focusIndex,
    position,
    left,
    right,
    top,
    scrollFocusedIntoView,
    isOpened,
    onSelect,
    ignoreAccents,
    containerRef,
    children,
    customSuggestionsContainer,
    onMouseDown,
    onMouseEnter,
    cls,
}) {
    const [ulElement, setUlElement] = useState()

    useEffect(() => {
        if (!ulElement || ulElement.offsetHeight >= ulElement.scrollHeight || !scrollFocusedIntoView) return
        const scrollTop = ulElement.scrollTop
        let { top, bottom } = ulElement.children[focusIndex].getBoundingClientRect()
        const { top: topContainer } = ulElement.getBoundingClientRect()
        top = top - topContainer + scrollTop
        bottom = bottom - topContainer + scrollTop
        if (top < scrollTop) ulElement.scrollTop = top
        else if (bottom > ulElement.offsetHeight) ulElement.scrollTop = bottom - ulElement.offsetHeight
    }, [focusIndex, scrollFocusedIntoView, ulElement])

    if (!isOpened) return null

    const items = flatSuggestions(suggestions).map(({ result, queryInfo }, index) => (
        <Suggestion
            key={`${queryInfo.childIndex}-${typeof result === "string" ? result : result.id}`}
            id={`${id}-${index}`}
            query={queryInfo.query}
            index={index}
            ignoreAccents={ignoreAccents}
            renderSuggestion={mentionProps(Children.toArray(children)[queryInfo.childIndex]).renderSuggestion}
            suggestion={result}
            focused={index === focusIndex}
            onClick={() => onSelect(result, queryInfo)}
            onMouseEnter={() => onMouseEnter(index)}
            cls={cls}
        />
    ))
    const list = (
        <ul ref={setUlElement} id={id} role="listbox" aria-label={a11ySuggestionsListLabel} style={STYLE.list} className={cls("suggestions", "list")}>
            {items}
        </ul>
    )

    return (
        <div
            style={{ position: position || "absolute", left, right, top, ...STYLE.suggestions }}
            className={cls("suggestions")}
            onMouseDown={onMouseDown}
            ref={containerRef}
        >
            {customSuggestionsContainer ? customSuggestionsContainer(list) : list}
        </div>
    )
}

const KEY = { TAB: 9, RETURN: 13, ESC: 27, UP: 38, DOWN: 40 }

// Props MentionsInput consumes; everything else goes on the textarea
const OWN_PROPS = [
    "style",
    "classNames",
    "className",
    "singleLine",
    "allowSpaceInQuery",
    "allowSuggestionsAboveCursor",
    "forceSuggestionsAboveCursor",
    "ignoreAccents",
    "a11ySuggestionsListLabel",
    "value",
    "onKeyDown",
    "customSuggestionsContainer",
    "onSelect",
    "onBlur",
    "onChange",
    "suggestionsPortalHost",
    "inputRef",
    "children",
]

let isComposing = false

export class MentionsInput extends React.Component {
    suggestions = {}
    _queryId = 0
    uuidSuggestionsOverlay = Math.random().toString(16).substring(2)

    state = {
        focusIndex: 0,
        selectionStart: null,
        selectionEnd: null,
        suggestions: {},
        caretPosition: null,
        suggestionsPosition: {},
        setSelectionAfterHandlePaste: false,
    }

    componentDidMount() {
        document.addEventListener("copy", this.handleCopy)
        document.addEventListener("cut", this.handleCut)
        document.addEventListener("paste", this.handlePaste)
        this.updateSuggestionsPosition()
    }

    componentDidUpdate(prevProps, prevState) {
        // unless this update came from a position change
        if (prevState.suggestionsPosition === this.state.suggestionsPosition) this.updateSuggestionsPosition()
        // keep the caret in place when a mention is added or removed
        if (this.state.setSelectionAfterMentionChange) {
            this.setState({ setSelectionAfterMentionChange: false })
            this.setSelection(this.state.selectionStart, this.state.selectionEnd)
        }
        if (this.state.setSelectionAfterHandlePaste) {
            this.setState({ setSelectionAfterHandlePaste: false })
            this.setSelection(this.state.selectionStart, this.state.selectionEnd)
        }
    }

    componentWillUnmount() {
        document.removeEventListener("copy", this.handleCopy)
        document.removeEventListener("cut", this.handleCut)
        document.removeEventListener("paste", this.handlePaste)
    }

    config = () => readConfigFromChildren(this.props.children)

    cls = (...path) => classesFor(this.props.className)(...path)

    getPlainText = () => getPlainText(this.props.value || "", this.config())

    isOpened = () => isNumber(this.state.selectionStart) && countSuggestions(this.state.suggestions) !== 0

    executeOnChange = (event, ...args) => this.props.onChange && this.props.onChange(event, ...args)

    setContainerElement = (el) => (this.containerElement = el)
    setHighlighterElement = (el) => (this.highlighterElement = el)
    setSuggestionsElement = (el) => (this.suggestionsElement = el)

    setInputRef = (el) => {
        this.inputElement = el
        const { inputRef } = this.props
        if (typeof inputRef === "function") inputRef(el)
        else if (inputRef) inputRef.current = el
    }

    getInputProps = () => {
        const { readOnly, disabled } = this.props
        const props = {}
        for (const [k, v] of Object.entries(this.props)) if (!OWN_PROPS.includes(k) && v !== undefined) props[k] = v
        return {
            ...props,
            style: this.props.singleLine ? STYLE.input : INPUT_MULTI,
            className: this.cls("input"),
            value: this.getPlainText(),
            onScroll: this.updateHighlighterScroll,
            ...(!readOnly &&
                !disabled && {
                    onChange: this.handleChange,
                    onSelect: this.handleSelect,
                    onKeyDown: this.handleKeyDown,
                    onBlur: this.handleBlur,
                    onCompositionStart: this.handleCompositionStart,
                    onCompositionEnd: this.handleCompositionEnd,
                }),
            ...(this.isOpened() && {
                role: "combobox",
                "aria-controls": this.uuidSuggestionsOverlay,
                "aria-expanded": true,
                "aria-autocomplete": "list",
                "aria-haspopup": "listbox",
                "aria-activedescendant": `${this.uuidSuggestionsOverlay}-${this.state.focusIndex}`,
            }),
        }
    }

    handleCaretPositionChange = (position) => this.setState({ caretPosition: position })

    handleChange = (ev) => {
        isComposing = false
        const value = this.props.value || ""
        const config = this.config()
        let newPlainTextValue = ev.target.value
        let selectionStartBefore = this.state.selectionStart
        if (selectionStartBefore == null) selectionStartBefore = ev.target.selectionStart
        let selectionEndBefore = this.state.selectionEnd
        if (selectionEndBefore == null) selectionEndBefore = ev.target.selectionEnd

        const newValue = applyChangeToValue(
            value,
            newPlainTextValue,
            { selectionStartBefore, selectionEndBefore, selectionEndAfter: ev.target.selectionEnd },
            config,
        )
        // a deleted mention takes the rest of its text with it
        newPlainTextValue = getPlainText(newValue, config)

        let selectionStart = ev.target.selectionStart
        let selectionEnd = ev.target.selectionEnd
        let setSelectionAfterMentionChange = false
        const startOfMention = findStartOfMentionInPlainText(value, config, selectionStart)
        if (startOfMention !== undefined && this.state.selectionEnd > startOfMention) {
            selectionStart = startOfMention + (ev.nativeEvent.data ? ev.nativeEvent.data.length : 0)
            selectionEnd = selectionStart
            setSelectionAfterMentionChange = true
        }
        this.setState({ selectionStart, selectionEnd, setSelectionAfterMentionChange })

        const mentions = getMentions(newValue, config)
        if (ev.nativeEvent.isComposing && selectionStart === selectionEnd) {
            this.updateMentionsQueries(this.inputElement.value, selectionStart)
        }
        this.executeOnChange({ target: { value: newValue } }, newValue, newPlainTextValue, mentions)
    }

    handleSelect = (ev) => {
        this.setState({ selectionStart: ev.target.selectionStart, selectionEnd: ev.target.selectionEnd })
        if (isComposing) return
        if (ev.target.selectionStart === ev.target.selectionEnd) this.updateMentionsQueries(this.inputElement.value, ev.target.selectionStart)
        else this.clearSuggestions()
        this.updateHighlighterScroll()
        this.props.onSelect && this.props.onSelect(ev)
    }

    handleKeyDown = (ev) => {
        // keys are the textarea's unless the list is open
        if (countSuggestions(this.state.suggestions) === 0 || !this.suggestionsElement) {
            this.props.onKeyDown && this.props.onKeyDown(ev)
            return
        }
        if (Object.values(KEY).includes(ev.keyCode)) {
            ev.preventDefault()
            ev.stopPropagation()
        }
        switch (ev.keyCode) {
            case KEY.ESC:
                return this.clearSuggestions()
            case KEY.DOWN:
                return this.shiftFocus(+1)
            case KEY.UP:
                return this.shiftFocus(-1)
            case KEY.RETURN:
            case KEY.TAB:
                return this.selectFocused()
            default:
        }
    }

    shiftFocus = (delta) => {
        const count = countSuggestions(this.state.suggestions)
        this.setState({ focusIndex: (count + this.state.focusIndex + delta) % count, scrollFocusedIntoView: true })
    }

    selectFocused = () => {
        const { result, queryInfo } = flatSuggestions(this.state.suggestions)[this.state.focusIndex]
        this.addMention(result, queryInfo)
        this.setState({ focusIndex: 0 })
    }

    handleBlur = (ev) => {
        const clickedSuggestion = this._suggestionsMouseDown
        this._suggestionsMouseDown = false
        // a click on the list keeps the selection
        if (!clickedSuggestion) this.setState({ selectionStart: null, selectionEnd: null })
        window.setTimeout(() => this.updateHighlighterScroll(), 1)
        this.props.onBlur && this.props.onBlur(ev, clickedSuggestion)
    }

    handleSuggestionsMouseDown = () => (this._suggestionsMouseDown = true)

    handleSuggestionsMouseEnter = (focusIndex) => this.setState({ focusIndex, scrollFocusedIntoView: false })

    updateSuggestionsPosition = () => {
        const { caretPosition } = this.state
        const { suggestionsPortalHost, allowSuggestionsAboveCursor, forceSuggestionsAboveCursor } = this.props
        const suggestions = this.suggestionsElement
        const highlighter = this.highlighterElement
        if (!caretPosition || !suggestions) return

        const caretOffsetParentRect = highlighter.getBoundingClientRect()
        const caretHeight = lengthProp(highlighter, "font-size")
        const viewportRelative = {
            left: caretOffsetParentRect.left + caretPosition.left,
            top: caretOffsetParentRect.top + caretPosition.top + caretHeight,
        }
        const viewportHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
        const position = {}

        if (suggestionsPortalHost) {
            position.position = "fixed"
            let left = viewportRelative.left - lengthProp(suggestions, "margin-left") - highlighter.scrollLeft
            let top = viewportRelative.top - lengthProp(suggestions, "margin-top") - highlighter.scrollTop
            const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
            position.left = left + suggestions.offsetWidth > viewportWidth ? Math.max(0, viewportWidth - suggestions.offsetWidth) : left
            position.top =
                (allowSuggestionsAboveCursor && top + suggestions.offsetHeight > viewportHeight && suggestions.offsetHeight < top - caretHeight) ||
                forceSuggestionsAboveCursor
                    ? Math.max(0, top - suggestions.offsetHeight - caretHeight)
                    : top
        } else {
            const left = caretPosition.left - highlighter.scrollLeft
            const top = caretPosition.top - highlighter.scrollTop
            if (left + suggestions.offsetWidth > this.containerElement.offsetWidth) position.right = 0
            else position.left = left
            position.top =
                (allowSuggestionsAboveCursor &&
                    viewportRelative.top - highlighter.scrollTop + suggestions.offsetHeight > viewportHeight &&
                    suggestions.offsetHeight < caretOffsetParentRect.top - caretHeight - highlighter.scrollTop) ||
                forceSuggestionsAboveCursor
                    ? top - suggestions.offsetHeight - caretHeight
                    : top
        }

        const prev = this.state.suggestionsPosition
        if (position.left === prev.left && position.top === prev.top && position.position === prev.position) return
        this.setState({ suggestionsPosition: position })
    }

    updateHighlighterScroll = () => {
        const input = this.inputElement
        const highlighter = this.highlighterElement
        // deferred calls can land after unmount
        if (!input || !highlighter) return
        highlighter.scrollLeft = input.scrollLeft
        highlighter.scrollTop = input.scrollTop
        highlighter.height = input.height
    }

    handleCompositionStart = () => (isComposing = true)
    handleCompositionEnd = () => (isComposing = false)

    setSelection = (selectionStart, selectionEnd) => {
        if (selectionStart === null || selectionEnd === null) return
        this.inputElement.setSelectionRange(selectionStart, selectionEnd)
    }

    updateMentionsQueries = (plainTextValue, caretPosition) => {
        // later results win; earlier async ones are dropped
        this._queryId++
        this.suggestions = {}
        this.setState({ suggestions: {} })

        const value = this.props.value || ""
        const config = this.config()
        const positionInValue = mapPlainTextIndex(value, config, caretPosition, "NULL")
        // no query with the caret inside a mention
        if (positionInValue === null) return

        // the text between the previous mention and the caret
        const substringStartIndex = getEndOfLastMention(value.substring(0, positionInValue), config)
        const substring = plainTextValue.substring(substringStartIndex, caretPosition)

        Children.forEach(this.props.children, (child, childIndex) => {
            if (!child) return
            const match = substring.match(makeTriggerRegex(mentionProps(child).trigger, this.props))
            if (match) {
                const querySequenceStart = substringStartIndex + substring.indexOf(match[1], match.index)
                this.queryData(match[2], childIndex, querySequenceStart, querySequenceStart + match[1].length, plainTextValue)
            }
        })
    }

    clearSuggestions = () => {
        this._queryId++
        this.suggestions = {}
        this.setState({ suggestions: {}, focusIndex: 0 })
    }

    queryData = (query, childIndex, querySequenceStart, querySequenceEnd, plainTextValue) => {
        const child = Children.toArray(this.props.children)[childIndex]
        const provideData = getDataProvider(mentionProps(child).data, this.props.ignoreAccents)
        const update = this.updateSuggestions.bind(null, this._queryId, childIndex, query, querySequenceStart, querySequenceEnd, plainTextValue)
        // arrays answer at once; a function may answer now or later through the callback
        const syncResult = provideData(query, update)
        if (Array.isArray(syncResult)) update(syncResult)
    }

    updateSuggestions = (queryId, childIndex, query, querySequenceStart, querySequenceEnd, plainTextValue, results) => {
        if (queryId !== this._queryId) return
        // kept on the instance so several sources in one tick don't overwrite each other
        this.suggestions = {
            ...this.suggestions,
            [childIndex]: { queryInfo: { childIndex, query, querySequenceStart, querySequenceEnd, plainTextValue }, results },
        }
        const { focusIndex } = this.state
        const count = countSuggestions(this.suggestions)
        this.setState({ suggestions: this.suggestions, focusIndex: focusIndex >= count ? Math.max(count - 1, 0) : focusIndex })
    }

    addMention = ({ id, display }, { childIndex, querySequenceStart, querySequenceEnd, plainTextValue }) => {
        const value = this.props.value || ""
        const config = this.config()
        const { markup, displayTransform, appendSpaceOnAdd, onAdd } = mentionProps(Children.toArray(this.props.children)[childIndex])
        const start = mapPlainTextIndex(value, config, querySequenceStart, "START")
        const end = start + querySequenceEnd - querySequenceStart
        let insert = makeMentionsMarkup(markup, id, display)
        if (appendSpaceOnAdd) insert += " "
        const newValue = spliceString(value, start, end, insert)

        this.inputElement.focus()

        let displayValue = displayTransform(id, display)
        if (appendSpaceOnAdd) displayValue += " "
        const newCaretPosition = querySequenceStart + displayValue.length
        this.setState({ selectionStart: newCaretPosition, selectionEnd: newCaretPosition, setSelectionAfterMentionChange: true })

        const mentions = getMentions(newValue, config)
        const newPlainTextValue = spliceString(plainTextValue, querySequenceStart, querySequenceEnd, displayValue)
        this.executeOnChange({ target: { value: newValue } }, newValue, newPlainTextValue, mentions)
        if (onAdd) onAdd(id, display, start, end)
        this.clearSuggestions()
    }

    handlePaste = (event) => {
        if (event.target !== this.inputElement || !event.clipboardData) return
        event.preventDefault()
        const { selectionStart, selectionEnd } = this.state
        const { value } = this.props
        const config = this.config()
        const markupStartIndex = mapPlainTextIndex(value, config, selectionStart, "START")
        const markupEndIndex = mapPlainTextIndex(value, config, selectionEnd, "END")
        const pastedMentions = event.clipboardData.getData("text/react-mentions")
        const pastedData = event.clipboardData.getData("text/plain")
        const newValue = spliceString(value, markupStartIndex, markupEndIndex, pastedMentions || pastedData).replace(/\r/g, "")
        const newPlainTextValue = getPlainText(newValue, config)
        this.executeOnChange({ target: { ...event.target, value: newValue } }, newValue, newPlainTextValue, getMentions(newValue, config))
        // caret to the end of the pasted text
        const startOfMention = findStartOfMentionInPlainText(value, config, selectionStart)
        const nextPos = (startOfMention || selectionStart) + getPlainText(pastedMentions || pastedData, config).length
        this.setState({ selectionStart: nextPos, selectionEnd: nextPos, setSelectionAfterHandlePaste: true })
    }

    saveSelectionToClipboard = (event) => {
        // the element's own selection, so copy works on disabled inputs too
        const { selectionStart, selectionEnd } = this.inputElement
        const { value } = this.props
        const config = this.config()
        const markupStartIndex = mapPlainTextIndex(value, config, selectionStart, "START")
        const markupEndIndex = mapPlainTextIndex(value, config, selectionEnd, "END")
        event.clipboardData.setData("text/plain", event.target.value.slice(selectionStart, selectionEnd))
        event.clipboardData.setData("text/react-mentions", value.slice(markupStartIndex, markupEndIndex))
    }

    handleCopy = (event) => {
        if (event.target !== this.inputElement || !event.clipboardData) return
        event.preventDefault()
        this.saveSelectionToClipboard(event)
    }

    handleCut = (event) => {
        if (event.target !== this.inputElement || !event.clipboardData) return
        event.preventDefault()
        this.saveSelectionToClipboard(event)
        const { selectionStart, selectionEnd } = this.state
        const { value } = this.props
        const config = this.config()
        const markupStartIndex = mapPlainTextIndex(value, config, selectionStart, "START")
        const markupEndIndex = mapPlainTextIndex(value, config, selectionEnd, "END")
        const newValue = value.slice(0, markupStartIndex) + value.slice(markupEndIndex)
        const newPlainTextValue = getPlainText(newValue, config)
        // react-mentions passed the plain text as target.value here, dropping every mention; the markup keeps them
        this.executeOnChange({ target: { ...event.target, value: newValue } }, newValue, newPlainTextValue, getMentions(value, config))
    }

    render() {
        const { className, singleLine, children, value, suggestionsPortalHost } = this.props
        const cls = this.cls
        const base = className ? className.split(" ")[0] : null
        const inputProps = this.getInputProps()

        const overlay = isNumber(this.state.selectionStart) ? (
            <SuggestionsOverlay
                id={this.uuidSuggestionsOverlay}
                position={this.state.suggestionsPosition.position}
                left={this.state.suggestionsPosition.left}
                top={this.state.suggestionsPosition.top}
                right={this.state.suggestionsPosition.right}
                focusIndex={this.state.focusIndex}
                scrollFocusedIntoView={this.state.scrollFocusedIntoView}
                containerRef={this.setSuggestionsElement}
                suggestions={this.state.suggestions}
                customSuggestionsContainer={this.props.customSuggestionsContainer}
                onSelect={this.addMention}
                onMouseDown={this.handleSuggestionsMouseDown}
                onMouseEnter={this.handleSuggestionsMouseEnter}
                isOpened={this.isOpened()}
                ignoreAccents={this.props.ignoreAccents}
                a11ySuggestionsListLabel={this.props.a11ySuggestionsListLabel}
                cls={cls}
            >
                {children}
            </SuggestionsOverlay>
        ) : null

        return (
            <div
                ref={this.setContainerElement}
                style={STYLE.root}
                className={className ? `${className} ${base}--${singleLine ? "singleLine" : "multiLine"}` : undefined}
            >
                <div className={cls("control")}>
                    <Highlighter
                        containerRef={this.setHighlighterElement}
                        value={value}
                        singleLine={singleLine}
                        selectionStart={this.state.selectionStart}
                        selectionEnd={this.state.selectionEnd}
                        onCaretPositionChange={this.handleCaretPositionChange}
                        cls={cls}
                    >
                        {children}
                    </Highlighter>
                    {singleLine ? <input type="text" ref={this.setInputRef} {...inputProps} /> : <textarea ref={this.setInputRef} {...inputProps} />}
                </div>
                {overlay && suggestionsPortalHost ? createPortal(overlay, suggestionsPortalHost) : overlay}
            </div>
        )
    }
}
